import { ShapeInstance } from '../../services/api';
import {
  ShapeType,
  ShapeToolSettings,
  generateShapePath,
  PathCommand,
  calculateShapeBounds,
  createDefaultShapeParams,
  getRectangleCornerHandles,
  getStarInnerRadiusHandle,
  RectangleParams,
  StarParams
} from '../../types/shape';
import { DASH_PATTERNS, DashPreset } from '../../types/penPath';

/**
 * Converts hex color to rgba
 */
function hexToRgba(hex: string, opacity: number): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (result) {
    const r = parseInt(result[1], 16);
    const g = parseInt(result[2], 16);
    const b = parseInt(result[3], 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
  return hex;
}

/**
 * Parses hex color to RGB components
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (result) {
    return {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    };
  }
  return null;
}

/**
 * Draws a feathered (soft) edge around a shape path using multiple strokes.
 * Each stroke's opacity is calculated so the maximum accumulated opacity
 * equals fillOpacity (not exceeding it).
 * @param ctx Canvas context
 * @param commands Path commands to stroke
 * @param fillColor Fill color in hex format
 * @param fillOpacity Base fill opacity (0-1)
 * @param featherRadius Radius of the feather effect in pixels
 */
function drawFeatheredEdge(
  ctx: CanvasRenderingContext2D,
  commands: PathCommand[],
  fillColor: string,
  fillOpacity: number,
  featherRadius: number
): void {
  const rgb = hexToRgb(fillColor);
  if (!rgb) return;

  // Number of steps for the gradient effect
  const steps = Math.min(Math.ceil(featherRadius / 2), 24);
  if (steps <= 0) return;

  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  // Draw strokes from outside to inside
  // Each stroke is drawn with opacity that creates a smooth gradient
  // The innermost strokes will be covered by the fill, so we only care about outer edge
  for (let i = steps; i >= 1; i--) {
    const t = i / steps; // 1 = outermost, 0 = innermost
    // Linear falloff scaled by fillOpacity - outermost is most transparent
    const alpha = fillOpacity * (1 - t) * 0.7;
    const strokeWidth = (featherRadius * 2) * t;

    ctx.beginPath();
    executePath(ctx, commands);
    ctx.strokeStyle = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
    ctx.lineWidth = strokeWidth;
    ctx.stroke();
  }
}

/**
 * Executes path commands on a canvas context
 */
function executePath(ctx: CanvasRenderingContext2D, commands: PathCommand[]): void {
  for (const cmd of commands) {
    switch (cmd.type) {
      case 'moveTo':
        ctx.moveTo(cmd.x, cmd.y);
        break;
      case 'lineTo':
        ctx.lineTo(cmd.x, cmd.y);
        break;
      case 'bezierCurveTo':
        ctx.bezierCurveTo(cmd.cp1x, cmd.cp1y, cmd.cp2x, cmd.cp2y, cmd.x, cmd.y);
        break;
      case 'quadraticCurveTo':
        ctx.quadraticCurveTo(cmd.cpx, cmd.cpy, cmd.x, cmd.y);
        break;
      case 'closePath':
        ctx.closePath();
        break;
    }
  }
}

/**
 * Transforms path commands to screen coordinates
 */
function transformCommands(
  commands: PathCommand[],
  zoom: number,
  offsetX: number,
  offsetY: number,
  centerX: number,
  centerY: number,
  shapeX: number,
  shapeY: number,
  rotation: number,
  scale: number
): PathCommand[] {
  const rad = (rotation * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);

  const transform = (x: number, y: number): { x: number; y: number } => {
    // Translate to origin (relative to shape center)
    let dx = x - shapeX;
    let dy = y - shapeY;

    // Apply scale
    dx *= scale;
    dy *= scale;

    // Apply rotation
    const rx = dx * cos - dy * sin;
    const ry = dx * sin + dy * cos;

    // Translate back and to screen coordinates
    const worldX = rx + shapeX;
    const worldY = ry + shapeY;

    return {
      x: (worldX - offsetX) * zoom + centerX,
      y: (worldY - offsetY) * zoom + centerY
    };
  };

  return commands.map(cmd => {
    switch (cmd.type) {
      case 'moveTo':
      case 'lineTo': {
        const t = transform(cmd.x, cmd.y);
        return { type: cmd.type, x: t.x, y: t.y };
      }
      case 'bezierCurveTo': {
        const cp1 = transform(cmd.cp1x, cmd.cp1y);
        const cp2 = transform(cmd.cp2x, cmd.cp2y);
        const end = transform(cmd.x, cmd.y);
        return {
          type: 'bezierCurveTo',
          cp1x: cp1.x, cp1y: cp1.y,
          cp2x: cp2.x, cp2y: cp2.y,
          x: end.x, y: end.y
        } as PathCommand;
      }
      case 'quadraticCurveTo': {
        const cp = transform(cmd.cpx, cmd.cpy);
        const end = transform(cmd.x, cmd.y);
        return {
          type: 'quadraticCurveTo',
          cpx: cp.x, cpy: cp.y,
          x: end.x, y: end.y
        } as PathCommand;
      }
      case 'closePath':
        return cmd;
      default:
        return cmd;
    }
  });
}

/**
 * Draws a feathered edge for inverted fill (soft edge going inward into the cutout)
 * @param ctx Canvas context
 * @param commands Path commands for the shape
 * @param fillColor Fill color in hex format
 * @param fillOpacity Base fill opacity (0-1)
 * @param featherRadius Radius of the feather effect in pixels
 */
function drawInvertedFeatheredEdge(
  ctx: CanvasRenderingContext2D,
  commands: PathCommand[],
  fillColor: string,
  fillOpacity: number,
  featherRadius: number
): void {
  const rgb = hexToRgb(fillColor);
  if (!rgb) return;

  // Number of steps for the gradient effect - more steps for larger radius
  const steps = Math.min(Math.ceil(featherRadius / 2), 48);
  if (steps <= 0) return;

  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  // Draw strokes from inside to outside (into the cutout area)
  // The strokes fade from solid to transparent as they go inward
  for (let i = 1; i <= steps; i++) {
    const t = i / steps; // 0 = innermost (edge), 1 = outermost (center of cutout)
    // Fade from fillOpacity at edge to 0 at center
    const alpha = fillOpacity * (1 - t);
    const strokeWidth = (featherRadius * 2) * t;

    ctx.beginPath();
    executePath(ctx, commands);
    ctx.strokeStyle = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
    ctx.lineWidth = strokeWidth;
    ctx.stroke();
  }
}

/**
 * Draws an inverted fill (everything except the shape is filled)
 * Uses canvas composite operations to create a cutout effect
 */
function drawInvertedFill(
  ctx: CanvasRenderingContext2D,
  screenCommands: PathCommand[],
  fillColor: string,
  fillOpacity: number,
  featherRadius: number,
  canvasWidth: number,
  canvasHeight: number
): void {
  // Save the current context state
  ctx.save();

  // Draw the full canvas rectangle
  ctx.beginPath();
  ctx.rect(0, 0, canvasWidth, canvasHeight);

  // Draw the shape path in reverse direction (counter-clockwise for cutout)
  // We use evenodd fill rule by drawing the shape on top
  executePath(ctx, screenCommands);

  // Use evenodd fill rule to create the cutout effect
  ctx.fillStyle = hexToRgba(fillColor, fillOpacity);
  ctx.fill('evenodd');

  ctx.restore();

  // Draw feathered edge going inward (into the transparent cutout area)
  if (featherRadius > 0) {
    drawInvertedFeatheredEdge(ctx, screenCommands, fillColor, fillOpacity, featherRadius);
  }
}

/**
 * Draws a complete shape
 */
export function drawShape(
  ctx: CanvasRenderingContext2D,
  shape: ShapeInstance,
  zoom: number,
  offsetX: number,
  offsetY: number,
  centerX: number,
  centerY: number,
  isSelected: boolean = false,
  isEditing: boolean = false,
  layerOpacity: number = 1
): void {
  // Create internal shape for path generation
  const internalShape = {
    id: shape.id,
    shapeType: shape.shapeType,
    shapeParams: shape.shapeParams,
    x: shape.x,
    y: shape.y,
    rotation: shape.rotation,
    scale: shape.scale,
    strokeColor: shape.strokeColor,
    strokeWidth: shape.strokeWidth,
    strokeOpacity: shape.strokeOpacity,
    lineCap: shape.lineCap,
    lineJoin: shape.lineJoin,
    dashPreset: shape.dashPreset,
    fillColor: shape.fillColor,
    fillOpacity: shape.fillOpacity,
    zIndex: shape.zIndex,
    layerId: shape.layerId
  } as import('../../types/shape').ShapeInstance;

  // Generate path commands
  const commands = generateShapePath(internalShape);
  if (commands.length === 0) return;

  // Transform to screen coordinates
  const screenCommands = transformCommands(
    commands,
    zoom,
    offsetX,
    offsetY,
    centerX,
    centerY,
    shape.x,
    shape.y,
    shape.rotation,
    shape.scale
  );

  // Draw the shape
  ctx.save();
  ctx.globalAlpha = layerOpacity;

  const featherRadius = (shape.featherRadius ?? 0) * zoom;
  const invertFill = shape.invertFill ?? false;

  // Fill if color is set
  if (shape.fillColor) {
    if (invertFill) {
      // Inverted fill: fill everything except the shape
      drawInvertedFill(
        ctx,
        screenCommands,
        shape.fillColor,
        shape.fillOpacity,
        featherRadius,
        ctx.canvas.width,
        ctx.canvas.height
      );
    } else {
      // Normal fill
      // Draw feathered edge FIRST (underneath the fill) so it extends outward
      if (featherRadius > 0) {
        drawFeatheredEdge(ctx, screenCommands, shape.fillColor, shape.fillOpacity, featherRadius);
      }

      // Then draw the solid fill on top
      ctx.beginPath();
      executePath(ctx, screenCommands);
      ctx.fillStyle = hexToRgba(shape.fillColor, shape.fillOpacity);
      ctx.fill();
    }
  }

  // Stroke if color is set (always draw stroke without blur for crisp edges)
  if (shape.strokeColor) {
    ctx.beginPath();
    executePath(ctx, screenCommands);
    ctx.strokeStyle = hexToRgba(shape.strokeColor, shape.strokeOpacity);
    ctx.lineWidth = shape.strokeWidth * zoom;
    ctx.lineCap = shape.lineCap as CanvasLineCap;
    ctx.lineJoin = shape.lineJoin as CanvasLineJoin;

    // Apply dash pattern
    const dashPattern = DASH_PATTERNS[shape.dashPreset as DashPreset];
    if (dashPattern) {
      ctx.setLineDash(dashPattern.map(d => d * shape.strokeWidth * zoom));
    } else {
      ctx.setLineDash([]);
    }

    ctx.stroke();
    ctx.setLineDash([]);
  }
  ctx.restore();

  // Draw selection UI
  if (isSelected) {
    drawShapeSelectionUI(ctx, internalShape, zoom, offsetX, offsetY, centerX, centerY, isEditing);
  }
}

/**
 * Draws selection UI for a shape (bounding box, handles, etc.)
 */
function drawShapeSelectionUI(
  ctx: CanvasRenderingContext2D,
  shape: import('../../types/shape').ShapeInstance,
  zoom: number,
  offsetX: number,
  offsetY: number,
  centerX: number,
  centerY: number,
  isEditing: boolean
): void {
  const bounds = calculateShapeBounds(shape);

  // Transform bounds corners to screen
  const rad = (shape.rotation * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);

  const transformPoint = (x: number, y: number): { x: number; y: number } => {
    let dx = (x - shape.x) * shape.scale;
    let dy = (y - shape.y) * shape.scale;
    const rx = dx * cos - dy * sin + shape.x;
    const ry = dx * sin + dy * cos + shape.y;
    return {
      x: (rx - offsetX) * zoom + centerX,
      y: (ry - offsetY) * zoom + centerY
    };
  };

  // Get screen coordinates for shape center
  const screenCenter = {
    x: (shape.x - offsetX) * zoom + centerX,
    y: (shape.y - offsetY) * zoom + centerY
  };

  // Calculate bounding box corners in world space (before rotation)
  const worldCorners = [
    { x: bounds.minX, y: bounds.minY }, // top-left
    { x: bounds.maxX, y: bounds.minY }, // top-right
    { x: bounds.maxX, y: bounds.maxY }, // bottom-right
    { x: bounds.minX, y: bounds.maxY }  // bottom-left
  ];

  // Transform to screen
  const screenCorners = worldCorners.map(c => transformPoint(c.x, c.y));

  // Draw bounding box
  ctx.strokeStyle = '#00aaff';
  ctx.lineWidth = 1;
  ctx.setLineDash([4, 4]);

  ctx.beginPath();
  ctx.moveTo(screenCorners[0].x, screenCorners[0].y);
  for (let i = 1; i < screenCorners.length; i++) {
    ctx.lineTo(screenCorners[i].x, screenCorners[i].y);
  }
  ctx.closePath();
  ctx.stroke();
  ctx.setLineDash([]);

  // Draw resize handles at corners and midpoints
  const handleSize = 8;

  // Corner handles
  for (const corner of screenCorners) {
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = '#00aaff';
    ctx.lineWidth = 2;
    ctx.fillRect(corner.x - handleSize / 2, corner.y - handleSize / 2, handleSize, handleSize);
    ctx.strokeRect(corner.x - handleSize / 2, corner.y - handleSize / 2, handleSize, handleSize);
  }

  // Midpoint handles
  for (let i = 0; i < screenCorners.length; i++) {
    const p1 = screenCorners[i];
    const p2 = screenCorners[(i + 1) % screenCorners.length];
    const mid = {
      x: (p1.x + p2.x) / 2,
      y: (p1.y + p2.y) / 2
    };

    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = '#00aaff';
    ctx.lineWidth = 2;
    ctx.fillRect(mid.x - handleSize / 2, mid.y - handleSize / 2, handleSize, handleSize);
    ctx.strokeRect(mid.x - handleSize / 2, mid.y - handleSize / 2, handleSize, handleSize);
  }

  // Draw rotation handle
  const topMid = {
    x: (screenCorners[0].x + screenCorners[1].x) / 2,
    y: (screenCorners[0].y + screenCorners[1].y) / 2
  };

  // Calculate direction perpendicular to top edge, pointing outward
  const rotHandleDistance = 25;
  const topEdgeAngle = Math.atan2(
    screenCorners[1].y - screenCorners[0].y,
    screenCorners[1].x - screenCorners[0].x
  );
  const outwardAngle = topEdgeAngle - Math.PI / 2;

  const rotHandle = {
    x: topMid.x + Math.cos(outwardAngle) * rotHandleDistance,
    y: topMid.y + Math.sin(outwardAngle) * rotHandleDistance
  };

  // Draw line to rotation handle
  ctx.strokeStyle = '#00aaff';
  ctx.lineWidth = 1;
  ctx.setLineDash([3, 3]);
  ctx.beginPath();
  ctx.moveTo(topMid.x, topMid.y);
  ctx.lineTo(rotHandle.x, rotHandle.y);
  ctx.stroke();
  ctx.setLineDash([]);

  // Draw rotation handle circle
  ctx.fillStyle = '#00aaff';
  ctx.beginPath();
  ctx.arc(rotHandle.x, rotHandle.y, handleSize / 2 + 2, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.arc(rotHandle.x, rotHandle.y, handleSize / 2 - 1, 0, Math.PI * 2);
  ctx.fill();

  // Draw corner radius handles for rectangles when editing
  if (isEditing && shape.shapeType === 'rectangle') {
    const cornerHandles = getRectangleCornerHandles(shape);
    for (const handle of cornerHandles) {
      const screenPos = transformPoint(handle.x, handle.y);

      // Draw corner radius handle (small diamond)
      ctx.fillStyle = handle.radius > 0 ? '#ffaa00' : '#888888';
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1;

      ctx.save();
      ctx.translate(screenPos.x, screenPos.y);
      ctx.rotate(Math.PI / 4);
      ctx.fillRect(-4, -4, 8, 8);
      ctx.strokeRect(-4, -4, 8, 8);
      ctx.restore();
    }
  }

  // Draw star inner radius handle when editing
  if (isEditing && shape.shapeType === 'star') {
    const innerHandle = getStarInnerRadiusHandle(shape);
    if (innerHandle) {
      const screenPos = transformPoint(innerHandle.x, innerHandle.y);

      // Draw inner radius handle (small circle)
      ctx.fillStyle = '#ff6600';
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2;

      ctx.beginPath();
      ctx.arc(screenPos.x, screenPos.y, 6, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    }
  }
}

/**
 * Draws a preview of the shape being created
 */
export function drawShapePreview(
  ctx: CanvasRenderingContext2D,
  shapeType: ShapeType,
  startPos: { x: number; y: number },
  currentPos: { x: number; y: number },
  settings: ShapeToolSettings,
  zoom: number,
  offsetX: number,
  offsetY: number,
  centerX: number,
  centerY: number,
  isShiftHeld: boolean = false,
  isAltHeld: boolean = false
): void {
  // Calculate dimensions
  let width = Math.abs(currentPos.x - startPos.x);
  let height = Math.abs(currentPos.y - startPos.y);

  // Shift = constrain proportions (square/circle)
  if (isShiftHeld) {
    const size = Math.max(width, height);
    width = size;
    height = size;
  }

  // Minimum size
  if (width < 10 && height < 10) return;

  // Calculate center position
  let cx: number, cy: number;

  if (isAltHeld) {
    // Draw from center
    cx = startPos.x;
    cy = startPos.y;
  } else {
    // Draw from corner
    const left = Math.min(startPos.x, currentPos.x);
    const top = Math.min(startPos.y, currentPos.y);

    if (isShiftHeld) {
      // Adjust for constrained proportions
      if (currentPos.x < startPos.x) {
        cx = startPos.x - width / 2;
      } else {
        cx = startPos.x + width / 2;
      }
      if (currentPos.y < startPos.y) {
        cy = startPos.y - height / 2;
      } else {
        cy = startPos.y + height / 2;
      }
    } else {
      cx = left + width / 2;
      cy = top + height / 2;
    }
  }

  // Create temporary shape for preview
  const shapeParams = createDefaultShapeParams(shapeType, width, height);

  // Apply settings-based defaults for specific shapes
  if (shapeType === 'rectangle' && shapeParams.type === 'rectangle') {
    shapeParams.params.cornerRadius.topLeft = settings.rectangleCornerRadius;
    shapeParams.params.cornerRadius.uniform = true;
  } else if (shapeType === 'polygon' && shapeParams.type === 'polygon') {
    shapeParams.params.sides = settings.polygonSides;
  } else if (shapeType === 'star' && shapeParams.type === 'star') {
    shapeParams.params.points = settings.starPoints;
    shapeParams.params.innerRatio = settings.starInnerRatio;
  } else if (shapeType === 'arrow' && shapeParams.type === 'arrow') {
    shapeParams.params.style = settings.arrowStyle;
  } else if (shapeType === 'callout' && shapeParams.type === 'callout') {
    shapeParams.params.pointerPosition = settings.calloutPointerPosition;
  }

  const tempShape: import('../../types/shape').ShapeInstance = {
    id: 'preview',
    shapeType,
    shapeParams,
    x: cx,
    y: cy,
    rotation: 0,
    scale: 1,
    strokeColor: settings.strokeColor,
    strokeWidth: settings.strokeWidth,
    strokeOpacity: settings.strokeOpacity,
    lineCap: settings.lineCap,
    lineJoin: settings.lineJoin,
    dashPreset: settings.dashPreset,
    fillColor: settings.fillColor,
    fillOpacity: settings.fillOpacity * 0.5, // Dimmed for preview
    featherRadius: settings.featherRadius ?? 0,
    invertFill: settings.invertFill ?? false,
    zIndex: 0,
    layerId: 'default'
  };

  // Generate and draw path
  const commands = generateShapePath(tempShape);
  if (commands.length === 0) return;

  const screenCommands = transformCommands(
    commands,
    zoom,
    offsetX,
    offsetY,
    centerX,
    centerY,
    cx,
    cy,
    0,
    1
  );

  ctx.beginPath();
  executePath(ctx, screenCommands);

  // Fill with preview opacity
  if (settings.fillColor) {
    ctx.fillStyle = hexToRgba(settings.fillColor, settings.fillOpacity * 0.3);
    ctx.fill();
  }

  // Stroke with preview style
  ctx.strokeStyle = hexToRgba(settings.strokeColor, settings.strokeOpacity * 0.7);
  ctx.lineWidth = settings.strokeWidth * zoom;
  ctx.lineCap = settings.lineCap as CanvasLineCap;
  ctx.lineJoin = settings.lineJoin as CanvasLineJoin;
  ctx.setLineDash([4, 4]);
  ctx.stroke();
  ctx.setLineDash([]);

  // Draw dimension text
  const displayWidth = isAltHeld ? width * 2 : width;
  const displayHeight = isAltHeld ? height * 2 : height;

  const screenCx = (cx - offsetX) * zoom + centerX;
  const screenCy = (cy - offsetY) * zoom + centerY;

  ctx.font = '12px sans-serif';
  ctx.fillStyle = '#ffffff';
  ctx.strokeStyle = '#000000';
  ctx.lineWidth = 3;
  ctx.textAlign = 'center';

  const dimensionText = `${Math.round(displayWidth)} x ${Math.round(displayHeight)}`;
  ctx.strokeText(dimensionText, screenCx, screenCy + 4);
  ctx.fillText(dimensionText, screenCx, screenCy + 4);
}

/**
 * Gets handle info for hit testing on a selected shape
 */
export interface ShapeHandleInfo {
  type: 'resize-corner' | 'resize-edge' | 'rotate' | 'corner-radius' | 'star-inner';
  position: 'tl' | 'tr' | 'br' | 'bl' | 't' | 'r' | 'b' | 'l' | 'rotation' | string;
  x: number;
  y: number;
}

/**
 * Gets all handle positions for a shape in screen coordinates
 */
export function getShapeHandles(
  shape: ShapeInstance,
  zoom: number,
  offsetX: number,
  offsetY: number,
  centerX: number,
  centerY: number,
  isEditing: boolean
): ShapeHandleInfo[] {
  const handles: ShapeHandleInfo[] = [];

  // Create internal shape for bounds calculation
  const internalShape = {
    id: shape.id,
    shapeType: shape.shapeType,
    shapeParams: shape.shapeParams,
    x: shape.x,
    y: shape.y,
    rotation: shape.rotation,
    scale: shape.scale,
    strokeColor: shape.strokeColor,
    strokeWidth: shape.strokeWidth,
    strokeOpacity: shape.strokeOpacity,
    lineCap: shape.lineCap,
    lineJoin: shape.lineJoin,
    dashPreset: shape.dashPreset,
    fillColor: shape.fillColor,
    fillOpacity: shape.fillOpacity,
    zIndex: shape.zIndex,
    layerId: shape.layerId
  } as import('../../types/shape').ShapeInstance;

  const bounds = calculateShapeBounds(internalShape);

  const rad = (shape.rotation * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);

  const transformPoint = (x: number, y: number): { x: number; y: number } => {
    let dx = (x - shape.x) * shape.scale;
    let dy = (y - shape.y) * shape.scale;
    const rx = dx * cos - dy * sin + shape.x;
    const ry = dx * sin + dy * cos + shape.y;
    return {
      x: (rx - offsetX) * zoom + centerX,
      y: (ry - offsetY) * zoom + centerY
    };
  };

  // Corner resize handles
  const corners: Array<{ pos: 'tl' | 'tr' | 'br' | 'bl'; x: number; y: number }> = [
    { pos: 'tl', x: bounds.minX, y: bounds.minY },
    { pos: 'tr', x: bounds.maxX, y: bounds.minY },
    { pos: 'br', x: bounds.maxX, y: bounds.maxY },
    { pos: 'bl', x: bounds.minX, y: bounds.maxY }
  ];

  for (const corner of corners) {
    const screen = transformPoint(corner.x, corner.y);
    handles.push({
      type: 'resize-corner',
      position: corner.pos,
      x: screen.x,
      y: screen.y
    });
  }

  // Edge midpoint resize handles
  const edges: Array<{ pos: 't' | 'r' | 'b' | 'l'; x: number; y: number }> = [
    { pos: 't', x: (bounds.minX + bounds.maxX) / 2, y: bounds.minY },
    { pos: 'r', x: bounds.maxX, y: (bounds.minY + bounds.maxY) / 2 },
    { pos: 'b', x: (bounds.minX + bounds.maxX) / 2, y: bounds.maxY },
    { pos: 'l', x: bounds.minX, y: (bounds.minY + bounds.maxY) / 2 }
  ];

  for (const edge of edges) {
    const screen = transformPoint(edge.x, edge.y);
    handles.push({
      type: 'resize-edge',
      position: edge.pos,
      x: screen.x,
      y: screen.y
    });
  }

  // Rotation handle
  const topMid = transformPoint((bounds.minX + bounds.maxX) / 2, bounds.minY);
  const topEdge0 = transformPoint(bounds.minX, bounds.minY);
  const topEdge1 = transformPoint(bounds.maxX, bounds.minY);

  const rotHandleDistance = 25;
  const topEdgeAngle = Math.atan2(
    topEdge1.y - topEdge0.y,
    topEdge1.x - topEdge0.x
  );
  const outwardAngle = topEdgeAngle - Math.PI / 2;

  handles.push({
    type: 'rotate',
    position: 'rotation',
    x: topMid.x + Math.cos(outwardAngle) * rotHandleDistance,
    y: topMid.y + Math.sin(outwardAngle) * rotHandleDistance
  });

  // Corner radius handles for rectangles when editing
  if (isEditing && shape.shapeType === 'rectangle') {
    const cornerHandles = getRectangleCornerHandles(internalShape);
    for (const handle of cornerHandles) {
      const screen = transformPoint(handle.x, handle.y);
      handles.push({
        type: 'corner-radius',
        position: handle.corner,
        x: screen.x,
        y: screen.y
      });
    }
  }

  // Star inner radius handle when editing
  if (isEditing && shape.shapeType === 'star') {
    const innerHandle = getStarInnerRadiusHandle(internalShape);
    if (innerHandle) {
      const screen = transformPoint(innerHandle.x, innerHandle.y);
      handles.push({
        type: 'star-inner',
        position: 'inner',
        x: screen.x,
        y: screen.y
      });
    }
  }

  return handles;
}

/**
 * Finds which handle (if any) is at a screen position
 */
export function findHandleAtPosition(
  handles: ShapeHandleInfo[],
  screenX: number,
  screenY: number,
  tolerance: number = 8
): ShapeHandleInfo | null {
  for (const handle of handles) {
    const dist = Math.sqrt(
      Math.pow(screenX - handle.x, 2) +
      Math.pow(screenY - handle.y, 2)
    );
    if (dist <= tolerance) {
      return handle;
    }
  }
  return null;
}
